/**
 * Workspaces API - Manejo de espacios de trabajo
 */

import { callApi, apiCache, apiCallCounter } from '../apiClient';
import { logger } from '../logger';

/**
 * Fetches workspaces from the API based on department code
 * @param {string} departmentCode - The department code of the user (e.g. '14337')
 * @param {boolean} admin - Whether to fetch all workspaces in admin mode
 * @param {boolean} forceRefresh - Whether to force refresh data from API
 * @returns {Promise<Array>} - Array of non-deleted workspaces the user has access to.
 */
export async function fetchWorkspacesFromLambda(departmentCode, admin = false, forceRefresh = false) {
  try {
    // Create a cache key that combines departmentCode and admin status
    const cacheKey = `workspaces_${departmentCode}_${admin}`;
    
    // Check if there's an in-flight request for this combination
    if (!forceRefresh && apiCache.pendingRequests[cacheKey]) {
      logger.info(`Reusing in-flight request for workspaces: ${departmentCode}, admin=${admin}`);
      return apiCache.pendingRequests[cacheKey];
    }
    
    // Check memory cache if not forcing refresh
    if (!forceRefresh && apiCache.workspaces[cacheKey]) {
      logger.info(`Using cached workspaces for: ${departmentCode}, admin=${admin}`);
      return apiCache.workspaces[cacheKey];
    }
    
    apiCallCounter.fetchWorkspaces++;
    
    // Format the request body according to the API requirements
    // Important: The API expects 'code' as the parameter name, not 'departmentCode'
    const requestBody = { 
      code: departmentCode,
      admin: admin
    };
    
    // Create a promise for this request
    const requestPromise = (async () => {
      const data = await callApi('/proyectos/read', requestBody);
      
      logger.info('Respuesta de workspaces recibida:', data);
      
      // Parse the response which might have a nested JSON string in the body property
      let workspaces = [];
      
      if (data.body && typeof data.body === 'string') {
        try {
          const parsedBody = JSON.parse(data.body);
          
          if (parsedBody.items && Array.isArray(parsedBody.items)) {
            workspaces = parsedBody.items.filter(workspace => !workspace.deleted);
          } else if (Array.isArray(parsedBody)) {
            workspaces = parsedBody.filter(workspace => !workspace.deleted);
          }
        } catch (e) {
          logger.error('Error parsing workspaces response body:', e);
        }
      } else if (Array.isArray(data)) {
        workspaces = data.filter(workspace => !workspace.deleted);
      } else if (data.items && Array.isArray(data.items)) {
        workspaces = data.items.filter(workspace => !workspace.deleted);
      }
      
      // Normalizar permisos (restricciones del tablero). Para que las restricciones funcionen,
      // el Lambda de proyectos/read debe devolver el atributo permisos (o Permisos) para cada proyecto.
      workspaces = workspaces.map(ws => ({
        ...ws,
        permisos: ws.permisos ?? ws.Permisos ?? null
      }));
      
      // Store in memory cache
      apiCache.workspaces[cacheKey] = workspaces;
      
      return workspaces;
    })();
    
    // Store the pending request
    apiCache.pendingRequests[cacheKey] = requestPromise;
    
    // When request completes, remove it from pending
    requestPromise.finally(() => {
      delete apiCache.pendingRequests[cacheKey];
    });
    
    return requestPromise;
  } catch (error) {
    logger.error('Error fetching workspaces from API:', error);
    return [];
  }
}

/**
 * Creates a new workspace/board in DynamoDB through the API
 * @param {string} uuid - El UUID ahora es opcional, Lambda lo generará
 * @param {string} workspace - The workspace name
 * @param {string} code - The code for the new workspace (custom input or departmentCode)
 * @param {string} departmentCode - El código de departamento del usuario como fallback
 * @param {Object|string} [permisos] - Columna Permisos del proyecto (objeto o JSON string)
 * @returns {Promise<Object>} - The response from the API
 */
export async function createWorkspaceInLambda(uuid, workspace, code, departmentCode, permisos = null) {
  try {
    apiCallCounter.createWorkspace++;
    logger.important(`API Call - createWorkspace(${workspace}): ${apiCallCounter.createWorkspace}`);
    
    if (!workspace) {
      throw new Error('Se requiere nombre de workspace válido');
    }
    
    // Si no hay code personalizado, usar el departmentCode como predeterminado
    const finalCode = code || departmentCode || '';
    
    if (!finalCode) {
      throw new Error('Se requiere código válido o código de departamento');
    }
    
    logger.info(`Creando nuevo workspace: ${workspace} con code: ${finalCode}`);
    
    // Format the request body according to the API requirements
    const requestBody = {
      code: finalCode,
      workspace,
      deleted: false
    };
    if (permisos !== undefined && permisos !== null) {
      requestBody.permisos = typeof permisos === 'string' ? permisos : JSON.stringify(permisos);
    }
    
    const data = await callApi('/proyectos/create', requestBody);
    
    return data;
  } catch (error) {
    logger.error('Error creating workspace in Lambda:', error);
    throw error;
  }
}

/**
 * Updates a workspace using the proyectos/update endpoint
 * @param {string} uuid - The UUID of the workspace to update
 * @param {Object} updates - Object containing the fields to update (code, workspace, permisos, etc.)
 * @returns {Promise<Object>} - The response from the API
 */
export async function updateWorkspaceInLambda(uuid, updates) {
  try {
    logger.important(`API Call - updateWorkspace with UUID: ${uuid}`);
    
    if (!uuid) {
      throw new Error('Se requiere un UUID válido para actualizar el workspace');
    }
    
    if (!updates || typeof updates !== 'object') {
      throw new Error('Se requiere un objeto de actualizaciones válido');
    }
    
    const payload = { uuid, ...updates };
    if (payload.permisos !== undefined && payload.permisos !== null) {
      payload.permisos = typeof payload.permisos === 'string' ? payload.permisos : JSON.stringify(payload.permisos);
    }
    
    logger.info(`Actualizando workspace con UUID ${uuid}:`, updates);
    
    const requestBody = payload;
    
    const data = await callApi('/proyectos/update', requestBody);
    
    // Invalidate the workspace cache after update
    Object.keys(apiCache.workspaces).forEach(key => {
      if (key.startsWith('workspaces_')) {
        delete apiCache.workspaces[key];
      }
    });
    
    return data;
  } catch (error) {
    logger.error('Error updating workspace in API:', error);
    throw error;
  }
}

/**
 * Deletes a workspace by marking it as deleted in DynamoDB
 * @param {string} uuid - The UUID of the workspace to delete
 * @returns {Promise<Object>} - The response from the API
 */
export async function deleteWorkspaceFromLambda(uuid) {
  try {
    apiCallCounter.deleteWorkspace++;
    logger.important(`API Call - deleteWorkspace: ${apiCallCounter.deleteWorkspace}`);
    
    if (!uuid) {
      throw new Error('Se requiere un UUID válido para eliminar el workspace');
    }
    
    logger.info(`Marcando workspace con UUID ${uuid} como eliminado`);
    
    // Format the request body according to the API requirements
    const requestBody = { uuid };
    
    const data = await callApi('/proyectos/delete', requestBody);
    
    // Invalidate all workspace caches since the deleted workspace should no longer appear
    Object.keys(apiCache.workspaces).forEach(key => {
      if (key.startsWith('workspaces_')) {
        delete apiCache.workspaces[key];
      }
    });
    
    return data;
  } catch (error) {
    logger.error('Error eliminando workspace en API:', error);
    throw error;
  }
} 